import { AnyStyledComponent } from 'styled-components'
import React, { FC, useCallback, useState, MouseEvent, useEffect } from 'react'

import CookieManagerService from '../../../services/cookie-manager-service'
import { Container, WishlistIcon, Spinner } from './styles'
import { getFavoriteListings, getIsLoggedIn } from '../../../redux/user/selectors'
import { getGuestFavoriteListings } from '../../../redux/wishlist/selectors'
import { useDispatch, useSelector } from 'react-redux'
import listingClient from '../../../clients/listing-client'
import HeartWishlistIcon from '../../../assets/icons/object-icons/heart.svg'
import PinkHeartIcon from '../../../assets/icons/object-icons/heart-pink.svg'
import { useRequestUserConfigContext } from '../../../hooks/request'
import {
    decrementCountTransport,
    incrementCountTransport,
    initGuestFavorites,
    addGuestFavorite,
    removeGuestFavorite,
} from '../../../redux/wishlist/actions'
import { addFavoriteListing, removeFavoriteListing } from '../../../redux/user/actions'

interface Props {
    containerBackgroundColor?: string
    containerBorderRadius?: string
    containerWidth?: string
    containerHeight?: string
    containerMargin?: string
    containerPadding?: string
    width?: string
    height?: string
    listingId: string
    containerborderColor?: string
    showBoxShadow?: boolean
    strokeColor?: string
}

const WishlistSection: FC<Props> = ({
    containerBackgroundColor,
    containerBorderRadius,
    containerHeight,
    containerWidth,
    containerMargin,
    containerPadding,
    width,
    height,
    listingId,
    containerborderColor,
    showBoxShadow = true,
    strokeColor,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    // Selectors for logged-in users (reads from user transport data)
    const userFavoriteListings = useSelector(getFavoriteListings)
    const isLoggedIn = useSelector(getIsLoggedIn)
    // Selector for logged-out users (reads from wishlist Redux state)
    const guestFavoriteListings = useSelector(getGuestFavoriteListings)
    const user = useRequestUserConfigContext()
    const dispatch = useDispatch()

    // Initialize guest favorites from cookies on mount (for logged-out users)
    useEffect(() => {
        if (!isLoggedIn) {
            const cookieValue = CookieManagerService.getCookie(CookieManagerService.FAVORITE_LISTINGS_IDS)
            if (cookieValue) {
                const cookieFavorites = cookieValue.split(',').filter(id => id.length > 0)
                if (cookieFavorites.length > 0) {
                    dispatch(initGuestFavorites(cookieFavorites))
                }
            }
        }
    }, [isLoggedIn, dispatch])

    // Combine wishlist state: logged-in uses userFavoriteListings, logged-out uses guestFavoriteListings
    const isWishlist = isLoggedIn ? userFavoriteListings.includes(listingId) : guestFavoriteListings.includes(listingId)

    const handleWishlistChange = useCallback(
        async (event: MouseEvent | TouchEvent) => {
            event.preventDefault()
            event.stopPropagation()

            if (isLoading) {
                return
            } // Prevent double clicks

            setIsLoading(true)
            try {
                if (isLoggedIn) {
                    // Logged-in user: call API and update user Redux state
                    await (isWishlist
                        ? listingClient.removeFromWishlist(user, listingId)
                        : listingClient.addToWishlist(user, listingId))
                    isWishlist ? dispatch(removeFavoriteListing(listingId)) : dispatch(addFavoriteListing(listingId))
                } else {
                    // Logged-out user: update wishlist Redux state and sync to cookies
                    if (isWishlist) {
                        dispatch(removeGuestFavorite(listingId))
                        CookieManagerService.removeValueFromCookieList(
                            CookieManagerService.FAVORITE_LISTINGS_IDS,
                            listingId,
                        )
                    } else {
                        dispatch(addGuestFavorite(listingId))
                        CookieManagerService.appendValueInCookieList(
                            CookieManagerService.FAVORITE_LISTINGS_IDS,
                            listingId,
                        )
                    }
                    isWishlist ? dispatch(decrementCountTransport()) : dispatch(incrementCountTransport())
                }
            } finally {
                setIsLoading(false)
            }
        },
        [user, isWishlist, listingId, isLoggedIn, isLoading, dispatch],
    )

    return (
        <Container
            onClick={handleWishlistChange}
            containerBackgroundColor={containerBackgroundColor}
            containerBorderRadius={containerBorderRadius}
            containerHeight={containerHeight}
            containerWidth={containerWidth}
            containerMargin={containerMargin}
            containerPadding={containerPadding}
            containerborderColor={containerborderColor}
            isLoading={isLoading}
            showBoxShadow={showBoxShadow}
        >
            {isLoading ? (
                <Spinner />
            ) : isWishlist ? (
                <WishlistIcon
                    as={PinkHeartIcon as AnyStyledComponent}
                    alt="wishlist icon"
                    width={width}
                    height={height}
                />
            ) : (
                <WishlistIcon
                    as={HeartWishlistIcon as AnyStyledComponent}
                    alt="wishlist icon"
                    width={width}
                    height={height}
                    strokeColor={strokeColor}
                />
            )}
        </Container>
    )
}

export default WishlistSection
